// Sky Basket collage layout templates.
// Each template returns an array of CSS style objects positioned in %.
// slots = maximum number of images the template accepts.

function buildGrid(n, forcedCols, forcedRows) {
  if (n <= 0) return [];
  const cols = forcedCols || Math.ceil(Math.sqrt(n));
  const rows = forcedRows || Math.ceil(n / cols);
  const out = [];
  for (let i = 0; i < n; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    out.push({
      left: `${(c * 100) / cols}%`,
      top: `${(r * 100) / rows}%`,
      width: `${100 / cols}%`,
      height: `${100 / rows}%`,
    });
  }
  return out;
}

// Rectangular grid preferring landscape ratio when possible.
function buildRectangleGrid(n) {
  if (n <= 0) return [];
  // Prefer rows > cols (rectangle) for A4 portrait
  const rows = Math.ceil(Math.sqrt(n * 1.414));
  const cols = Math.ceil(n / rows);
  return buildGrid(n, cols, rows);
}

function buildStripRows(rows) {
  const out = [];
  for (let r = 0; r < rows; r++) {
    out.push({
      left: "0%",
      top: `${(r * 100) / rows}%`,
      width: "100%",
      height: `${100 / rows}%`,
    });
  }
  return out;
}

export const TEMPLATES = {
  auto: {
    id: "auto",
    label: "Auto Grid",
    slots: null,
    build: (n) => buildGrid(n),
  },
  autoSquare: {
    id: "autoSquare",
    label: "Auto Grid (Square)",
    slots: null,
    // Aim for each cell to be roughly square on A4 portrait.
    // On A4 the page is 1.414× taller than wide, so we need more rows
    // than columns to keep each cell close to a square.
    build: (n) => {
      if (n <= 0) return [];
      const rows = Math.max(1, Math.round(Math.sqrt(n * 1.414)));
      const cols = Math.max(1, Math.ceil(n / rows));
      return buildGrid(n, cols, rows);
    },
  },
  autoRect: {
    id: "autoRect",
    label: "Auto Grid (Rectangle)",
    slots: null,
    // Standard sqrt grid — each cell inherits A4 portrait aspect,
    // giving rectangular (taller-than-wide) cells.
    build: (n) => buildGrid(n),
  },
  single: {
    id: "single",
    label: "Single",
    slots: 1,
    build: () => buildGrid(1),
  },
  duoHorizontal: {
    id: "duoHorizontal",
    label: "Duo Horizontal",
    slots: 2,
    build: () => buildGrid(2, 1, 2),
  },
  duoVertical: {
    id: "duoVertical",
    label: "Duo Vertical",
    slots: 2,
    build: () => buildGrid(2, 2, 1),
  },
  quad: {
    id: "quad",
    label: "Quad",
    slots: 4,
    build: () => buildGrid(4, 2, 2),
  },
  strip2: {
    id: "strip2",
    label: "Strip (2 Rows)",
    slots: 2,
    build: () => buildStripRows(2),
  },
  strip3: {
    id: "strip3",
    label: "Strip (3 Rows)",
    slots: 3,
    build: () => buildStripRows(3),
  },
  strip4: {
    id: "strip4",
    label: "Strip (4 Rows)",
    slots: 4,
    build: () => buildStripRows(4),
  },
  six: {
    id: "six",
    label: "Six",
    slots: 6,
    build: () => buildGrid(6, 2, 3),
  },
  featurePlus3: {
    id: "featurePlus3",
    label: "Feature + 3",
    slots: 4,
    build: () => [
      { left: "0%", top: "0%", width: "66.6667%", height: "100%" },
      { left: "66.6667%", top: "0%", width: "33.3333%", height: "33.3333%" },
      { left: "66.6667%", top: "33.3333%", width: "33.3333%", height: "33.3333%" },
      { left: "66.6667%", top: "66.6667%", width: "33.3333%", height: "33.3333%" },
    ],
  },
  heroStrip: {
    id: "heroStrip",
    label: "Hero + Strip",
    slots: 5,
    build: () => [
      { left: "0%", top: "0%", width: "100%", height: "60%" },
      { left: "0%", top: "60%", width: "25%", height: "40%" },
      { left: "25%", top: "60%", width: "25%", height: "40%" },
      { left: "50%", top: "60%", width: "25%", height: "40%" },
      { left: "75%", top: "60%", width: "25%", height: "40%" },
    ],
  },
  nine: {
    id: "nine",
    label: "Nine",
    slots: 9,
    build: () => buildGrid(9, 3, 3),
  },
};

// Template ids arranged in a friendly order for the dropdown.
export const TEMPLATE_LIST = [
  "auto",
  "autoSquare",
  "autoRect",
  "single",
  "duoHorizontal",
  "duoVertical",
  "quad",
  "strip2",
  "strip3",
  "strip4",
  "six",
  "featurePlus3",
  "heroStrip",
  "nine",
];

// Get the effective slot count for a template given a target photo count.
export function getSlotCount(templateId, photosPerPage) {
  const t = TEMPLATES[templateId];
  if (!t) return photosPerPage;
  if (t.slots === null) return photosPerPage; // auto* variants
  return t.slots;
}

// Build layout slots for the given template and photo count.
export function getLayoutSlots(templateId, photosPerPage) {
  const t = TEMPLATES[templateId] || TEMPLATES.auto;
  return t.build(photosPerPage);
}
